import { useState, useEffect } from 'react';
import { X, Bug, Lightbulb, Heart, Send, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Quick "users-as-debuggers" feedback bubble. Fires after 20s of browsing,
// once per session. Three-tap funnel: kind → message → submit. Posts to
// /api/feedback which routes to notifyAdminEvent (Telegram + email).
export default function FeedbackBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(
    () => !!sessionStorage.getItem('feedbackBannerDismissed')
  );
  const [step, setStep] = useState('intro'); // intro | form | thanks
  const [kind, setKind] = useState(null);    // 'bug' | 'idea' | 'love'
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isDismissed) return;
    const timer = setTimeout(() => setIsVisible(true), 20000);
    return () => clearTimeout(timer);
  }, [isDismissed]);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    sessionStorage.setItem('feedbackBannerDismissed', 'true');
  };

  const pickKind = (k) => {
    setKind(k);
    if (k === 'love') {
      // No form needed for praise — fire & forget, then thank.
      submit({ kind: k, message: '', contact: '' });
    } else {
      setStep('form');
    }
  };

  const submit = async (override) => {
    const payload = override || { kind, message: message.trim(), contact: contact.trim() };
    if (!payload.kind) return;
    setSubmitting(true);
    try {
      await fetch(`${API}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...payload,
          path: typeof window !== 'undefined' ? window.location.pathname + window.location.search : '',
          referrer: typeof document !== 'undefined' ? document.referrer : '',
          ua: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        }),
      });
    } catch (_) { /* ignore — best effort */ }
    setSubmitting(false);
    setStep('thanks');
    // Auto-dismiss the thank-you after 3s.
    setTimeout(() => {
      handleDismiss();
    }, 3000);
  };

  if (isDismissed || !isVisible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-50 animate-fb-slide-up">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl p-4 relative overflow-hidden">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 hover:bg-slate-100 rounded-full transition-colors"
          aria-label="Κλείσιμο"
        >
          <X className="w-4 h-4 text-slate-400" />
        </button>

        {step === 'intro' && (
          <div>
            <div className="flex items-start gap-3 pr-6">
              <div className="text-2xl">👋</div>
              <div>
                <h3 className="font-bold text-slate-900 mb-0.5">Πώς πάει η εμπειρία σου;</h3>
                <p className="text-xs text-slate-500">30 δευτερόλεπτα — μας βοηθάς πολύ</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3">
              <button
                onClick={() => pickKind('bug')}
                className="flex flex-col items-center gap-1 p-3 rounded-xl border border-slate-200 hover:border-red-400 hover:bg-red-50 transition-colors"
              >
                <Bug className="w-5 h-5 text-red-500" />
                <span className="text-xs font-medium text-slate-700">Bug</span>
              </button>
              <button
                onClick={() => pickKind('idea')}
                className="flex flex-col items-center gap-1 p-3 rounded-xl border border-slate-200 hover:border-amber-400 hover:bg-amber-50 transition-colors"
              >
                <Lightbulb className="w-5 h-5 text-amber-500" />
                <span className="text-xs font-medium text-slate-700">Ιδέα</span>
              </button>
              <button
                onClick={() => pickKind('love')}
                className="flex flex-col items-center gap-1 p-3 rounded-xl border border-slate-200 hover:border-pink-400 hover:bg-pink-50 transition-colors"
              >
                <Heart className="w-5 h-5 text-pink-500" />
                <span className="text-xs font-medium text-slate-700">Όλα καλά</span>
              </button>
            </div>
          </div>
        )}

        {step === 'form' && (
          <div>
            <div className="flex items-center gap-2 mb-3 pr-6">
              {kind === 'bug' ? <Bug className="w-5 h-5 text-red-500" /> : <Lightbulb className="w-5 h-5 text-amber-500" />}
              <h3 className="font-bold text-slate-900">
                {kind === 'bug' ? 'Πες μας τι δεν δουλεύει' : 'Τι θα ήθελες να δεις;'}
              </h3>
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              maxLength={1000}
              autoFocus
              placeholder={kind === 'bug' ? 'π.χ. πάτησα το κουμπί X και…' : 'π.χ. θα ήθελα να μπορώ να…'}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 resize-none"
            />
            <input
              type="text"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              maxLength={120}
              placeholder="Email/τηλέφωνο (προαιρετικό)"
              className="w-full mt-2 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
            />
            <div className="flex items-center justify-between mt-3">
              <button
                onClick={() => { setStep('intro'); setMessage(''); }}
                className="text-xs text-slate-500 hover:text-slate-700"
              >
                ← Πίσω
              </button>
              <button
                onClick={() => submit()}
                disabled={submitting || !message.trim()}
                className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                {submitting ? 'Αποστολή…' : 'Στείλε'}
              </button>
            </div>
          </div>
        )}

        {step === 'thanks' && (
          <div className="flex items-center gap-3 py-2 pr-6">
            <div className="p-2 bg-green-100 rounded-xl">
              <Check className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900">Ευχαριστούμε! 🙏</h3>
              <p className="text-xs text-slate-500">Το λάβαμε — θα το δούμε σύντομα.</p>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fb-slide-up {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-fb-slide-up {
          animation: fb-slide-up 0.35s ease-out;
        }
      `}</style>
    </div>
  );
}
